import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ReactComponent as PlanneryLogo } from 'images/plannery-logo.svg';
import styles from 'prototypes/securedCard/app.module.scss';
import { formatMoney } from 'prototypes/securedCard/formatMoney';
import { FLOW_STEPS, PrototypePath } from 'prototypes/securedCard/paths';
import { cadenceLabel, milestoneRank } from 'prototypes/securedCard/storyMath';
import { useStory } from 'prototypes/securedCard/StoryContext';

interface AppChromeProps {
  step: PrototypePath;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}

const AppChrome = ({ step, title, subtitle, children }: AppChromeProps) => {
  const navigate = useNavigate();
  const { state } = useStory();
  const currentIndex = FLOW_STEPS.findIndex((item) => item.path === step);
  const stepNumber = currentIndex + 1;

  return (
    <div className={styles.shell} data-testid={`secured-card-${step}`}>
      <p className={styles.banner}>Design prototype · Mock data · Savings earn 5% APY</p>
      <header className={styles.header}>
        <PlanneryLogo className={styles.logo} aria-label="Plannery" />
        <Link className={styles.headerLink} to={PrototypePath.Welcome}>
          Overview
        </Link>
      </header>
      <main className={styles.frame}>
        <div className={styles.progress} aria-hidden="true">
          {FLOW_STEPS.map((item) => {
            const reachable = milestoneRank(state.milestone) >= milestoneRank(item.required);
            const isCurrent = item.path === step;
            const className = isCurrent ? styles.progressCurrent : styles.progressSegment;
            const doneClass = !isCurrent && reachable ? styles.progressDone : styles.progressSegment;
            if (reachable && !isCurrent) {
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.progressSegment} ${doneClass}`}
                  aria-label={item.label}
                  onClick={() => navigate(item.path)}
                />
              );
            }
            return <span key={item.id} className={`${styles.progressSegment} ${className}`} />;
          })}
        </div>
        {stepNumber > 0 && (
          <p className={styles.stepLabel}>
            Step {stepNumber} of {FLOW_STEPS.length} · {FLOW_STEPS[currentIndex].label}
          </p>
        )}
        <p className={styles.customerLine}>
          {cadenceLabel(state.cadence)} · {formatMoney(state.perPaycheckCents)} from payroll
        </p>
        <section className={styles.panel}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
          {children}
        </section>
      </main>
    </div>
  );
};

export default AppChrome;
